import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { ListOrdered, MessageSquare } from 'lucide-react';
import AssistantChatPanel, { type SelectionRequiredEvent } from '../components/AssistantChatPanel';
import AssistantResultPanel from '../components/AssistantResultPanel';
import AssistantStateOverlay from '../components/AssistantStateOverlay';
import EntityDisambigModal from '../components/EntityDisambigModal';
import VorlagenSelectionModal from '../components/VorlagenSelectionModal';

/**
 * Full-Page-Variante des KI-Assistenten (Joerg-Wunsch 2026-05-22 als
 * Mobile-Home-Tab). Spiegelt die Drawer-Funktionalitaet 1:1 — Tab-Switch
 * Chat / Ergebnisse, Indikator beim eingehenden result_panel, Auto-Switch
 * nach Stream-Ende, Vorlagen- und Entity-Disambig-Modal.
 *
 * Eigener LS-Key `asst-page-tab` (separat vom Drawer), damit User Drawer-
 * und Page-Praeferenzen unabhaengig behalten.
 */

type Tab = 'chat' | 'results';
const LS_TAB = 'asst-page-tab';

export default function AssistantPage() {
  const [tab, setTabState] = useState<Tab>(() => {
    const stored = localStorage.getItem(LS_TAB);
    return stored === 'results' || stored === 'chat' ? (stored as Tab) : 'chat';
  });
  const setTab = useCallback((t: Tab) => {
    setTabState(t);
    localStorage.setItem(LS_TAB, t);
  }, []);

  const [conversationId, setConversationId] = useState<string | null>(null);
  const [activePanelId, setActivePanelId] = useState<string | null>(null);
  const [pendingPanelIds, setPendingPanelIds] = useState<string[]>([]);
  const [streaming, setStreaming] = useState(false);
  const [selectionModal, setSelectionModal] = useState<SelectionRequiredEvent | null>(null);
  const tabRef = useRef<Tab>(tab);
  useEffect(() => { tabRef.current = tab; }, [tab]);

  const handleConversationChange = useCallback((id: string | null) => {
    setConversationId(id);
    setActivePanelId(null);
    setPendingPanelIds([]);
  }, []);

  const handlePanelEvent = useCallback((panelId: string) => {
    setPendingPanelIds((prev) => [...prev, panelId]);
  }, []);

  const handleSelectionRequired = useCallback((ev: SelectionRequiredEvent) => {
    setSelectionModal(ev);
  }, []);

  const handleStreamingChange = useCallback((s: boolean) => {
    setStreaming(s);
    if (!s) {
      setPendingPanelIds((pending) => {
        if (pending.length > 0) {
          setActivePanelId(pending[pending.length - 1]!);
          if (tabRef.current === 'chat') setTab('results');
        }
        return [];
      });
    }
  }, [setTab]);

  const showPanelIndicator = streaming && pendingPanelIds.length > 0 && tab !== 'results';

  return (
    // 100dvh statt 100vh: iOS-Safari zieht die Adressleiste nicht vom
    // 100vh ab — die Page war damit hoeher als der sichtbare Viewport und
    // die Chat-Eingabe lag unten unsichtbar. `--portal-mobile-tabbar-h`
    // wird von MobileHomeTabBar dynamisch gesetzt (80px auf Mobile wenn
    // die Bar aktiv ist, sonst 0). Joerg 2026-05-26 — Assistent-Eingabe
    // war beim Oeffnen nicht im sichtbaren Bereich.
    <div className="flex flex-col" style={{ height: 'calc(100dvh - var(--portal-sticky-top, 56px) - var(--portal-mobile-tabbar-h, 0px))' }}>
      <div className="flex border-b border-default bg-muted shrink-0">
        <TabBtn active={tab === 'chat'} onClick={() => setTab('chat')} icon={<MessageSquare size={18} />}>
          Chat
        </TabBtn>
        <TabBtn
          active={tab === 'results'}
          onClick={() => setTab('results')}
          icon={
            <span className="relative inline-flex">
              <ListOrdered size={18} />
              {showPanelIndicator && (
                <span className="absolute -top-1 -right-1 h-2 w-2 rounded-full bg-blue-500 animate-pulse" />
              )}
            </span>
          }
        >
          Ergebnisse
        </TabBtn>
      </div>

      {/* Beide Panels gemountet halten — Tab-Switch ueber visibility (sonst geht
          der Chat-Scroll-State + Eingabe-Puffer verloren). */}
      <div className="flex-1 min-h-0 relative">
        <div className={`absolute inset-0 ${tab === 'chat' ? '' : 'hidden'}`}>
          <AssistantChatPanel
            onConversationChange={handleConversationChange}
            onPanelEvent={handlePanelEvent}
            onStreamingChange={handleStreamingChange}
            onSelectionRequired={handleSelectionRequired}
          />
          <AssistantStateOverlay />
        </div>
        <div className={`absolute inset-0 ${tab === 'results' ? '' : 'hidden'}`}>
          <AssistantResultPanel
            conversationId={conversationId}
            activePanelId={activePanelId}
            onActivePanelChange={setActivePanelId}
            isMobile={true}
            onClose={() => { /* Full-Page: nichts schliessen, navigate kommt vom Link selbst */ }}
          />
        </div>
      </div>

      {selectionModal && selectionModal.entity_choice && (
        <EntityDisambigModal
          templateSlug={selectionModal.template_slug}
          dataRefs={selectionModal.data_refs}
          entityChoice={selectionModal.entity_choice}
          onClose={() => setSelectionModal(null)}
          onCreated={() => { /* navigation uebernimmt das Modal selbst */ }}
          onContinueSelection={(ev) => setSelectionModal(ev)}
        />
      )}
      {selectionModal && !selectionModal.entity_choice && (
        <VorlagenSelectionModal
          templateSlug={selectionModal.template_slug}
          dataRefs={selectionModal.data_refs}
          sources={selectionModal.sources}
          onClose={() => setSelectionModal(null)}
          onCreated={() => { /* navigation zur Kundenakte uebernimmt das Modal selbst */ }}
        />
      )}
    </div>
  );
}

function TabBtn({ active, onClick, icon, children }: { active: boolean; onClick: () => void; icon: ReactNode; children: ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition flex-1 justify-center min-h-[48px] ${
        active
          ? 'border-blue-500 text-blue-600'
          : 'border-transparent text-muted-foreground hover:text-foreground'
      }`}
    >
      {icon}
      <span>{children}</span>
    </button>
  );
}
